// A small, generic focus-trapped confirm dialog - the SAME shell `GdprRoutingPanel`
// already built and tested (`.gdpr-routing-panel > .card`, GdprRoutingPanel.tsx:91-129;
// docs/phase-0/slice5-component-designs.md §2.6 "the ONE focus-trap panel visual this
// slice reuses"), at its smallest content size: one heading, one line of body text, a
// primary confirm and an explicit Cancel that always exits without acting
// (`escape-routes`). Reused by `BrandApprovalGate` (the mockup approval confirm, ux spec
// §6.3) and `PromoteConfirmDialog` (the production promote confirm, ux spec §9.3) rather
// than each re-implementing the identical focus-trap logic (Rule of Three -
// `GdprRoutingPanel` is the first instance, these are the second and third).
import { useEffect, useId, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const BuildConfirmDialog = ({
  heading,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  triggerRef,
  confirmClassName = "btn btn-primary",
}: {
  readonly heading: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** The control that opened the dialog - focus returns here on close
   * (`focus-management`). Optional: a caller with nothing to restore focus to simply
   * gets no restore. */
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
  /** Amber-primary by default; `PromoteConfirmDialog` explicitly wants the primary
   * amber fill too (promotion is a positive, intended action, never a risk-toned
   * button, component-designs.md §3.6) - kept overridable rather than hardcoded. */
  readonly confirmClassName?: string;
}): React.JSX.Element => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();

  useEffect(() => {
    headingRef.current?.focus();
    return () => {
      triggerRef?.current?.focus();
    };
  }, [triggerRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab" || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="gdpr-routing-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      ref={panelRef}
      onKeyDown={handleKeyDown}
      data-testid="build-confirm-dialog"
    >
      <div className="card pad">
        <h2 className="h3" id={headingId} tabIndex={-1} ref={headingRef}>
          {heading}
        </h2>
        <p className="muted mt8" style={{ fontSize: 13.5 }}>
          {body}
        </p>
        <div className="row gap12 mt16">
          <button type="button" className={confirmClassName} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
